import React from "react";
import { Box, TextField } from "@mui/material";
import LimitDynamicsTable from "./LimitDynamicsTable";
import LabeledTextField from "./LabeledTextField";
import SectionHeader from "./SectionHeader";

const CreditPolicyTab = ({ data, onUpdate }) => {
  const updateField = (path, value) => {
    onUpdate(path, value);
  };

  return (
    <Box>
      {/* Section: Credit Policy */}
      <Box sx={{ marginBottom: "32px" }}>
        <SectionHeader>Кредитная политика</SectionHeader>

        <LabeledTextField
          label="Кр. инспектор"
          value={data.creditPolicy?.creditInspector || ""}
          onChange={(val) => updateField("creditPolicy.creditInspector", val)}
        />

        <LabeledTextField
          label="Контроллер"
          value={data.creditPolicy?.controller || ""}
          onChange={(val) => updateField("creditPolicy.controller", val)}
        />

        {/* Two-column layout for Main Business Risks and Arguments For */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "24px",
          marginBottom: "24px"
        }}>
          {/* Main Business Risks */}
          <Box>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              ОСНОВНЫЕ РИСКИ БИЗНЕСА
            </Box>
            <TextField
              value={data.creditPolicy?.mainBusinessRisks || ""}
              onChange={(e) => updateField("creditPolicy.mainBusinessRisks", e.target.value)}
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          {/* Arguments For */}
          <Box>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              АРГУМЕНТЫ ЗА КРЕДИТОВАНИЕ КЛИЕНТА
            </Box>
            <TextField
              value={data.creditPolicy?.argumentsFor || ""}
              onChange={(e) => updateField("creditPolicy.argumentsFor", e.target.value)}
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Main Thesis */}
        <Box sx={{ marginBottom: "16px" }}>
          <Box sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#1A1E24",
            marginBottom: "8px"
          }}>
            ОСНОВНОЙ ТЕЗИС:
          </Box>
          <TextField
            value={data.creditPolicy?.mainThesis || ""}
            onChange={(e) => updateField("creditPolicy.mainThesis", e.target.value)}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
              },
            }}
          />
        </Box>

        {/* Limit Dynamics Table */}
        <Box sx={{ marginTop: "24px", marginBottom: "24px" }}>
          <LimitDynamicsTable
            data={data.creditPolicy?.limitDynamics || {}}
            onChange={(newData) => updateField("creditPolicy.limitDynamics", newData)}
          />
        </Box>

        {/* Increase Limit Reasons */}
        <Box sx={{ marginBottom: "16px" }}>
          <Box sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#1A1E24",
            marginBottom: "8px"
          }}>
            Причины увеличения лимита, предложения:
          </Box>
          <TextField
            value={data.creditPolicy?.increaseLimitReasons || ""}
            onChange={(e) => updateField("creditPolicy.increaseLimitReasons", e.target.value)}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
              },
            }}
          />
        </Box>

        {/* Increase Limit Conditions */}
        <Box sx={{ marginBottom: "16px" }}>
          <Box sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#1A1E24",
            marginBottom: "8px"
          }}>
            Условия увеличения лимита:
          </Box>
          <TextField
            value={data.creditPolicy?.increaseLimitConditions || ""}
            onChange={(e) => updateField("creditPolicy.increaseLimitConditions", e.target.value)}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
              },
            }}
          />
        </Box>

        {/* Subsection: Credit Terms */}
        <Box sx={{ marginTop: "32px", marginBottom: "16px" }}>
          <SectionHeader variant="secondary">
            ОСНОВНЫЕ УСЛОВИЯ КРЕДИТОВАНИЯ И ЧТО МЕНЯЕТСЯ:
          </SectionHeader>

          {/* Collateral and LTV */}
          <Box sx={{ marginBottom: "16px" }}>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Залоги и залоговые коэффициенты:
            </Box>
            <TextField
              value={data.creditPolicy?.collateralAndLtv || ""}
              onChange={(e) => updateField("creditPolicy.collateralAndLtv", e.target.value)}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          {/* Guarantees */}
          <Box sx={{ marginBottom: "16px" }}>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Поручительства:
            </Box>
            <TextField
              value={data.creditPolicy?.guarantees || ""}
              onChange={(e) => updateField("creditPolicy.guarantees", e.target.value)}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          {/* Interest Rates */}
          <Box sx={{ marginBottom: "16px" }}>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Процентные ставки:
            </Box>
            <TextField
              value={data.creditPolicy?.interestRates || ""}
              onChange={(e) => updateField("creditPolicy.interestRates", e.target.value)}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          {/* Tenors and Repayment */}
          <Box sx={{ marginBottom: "16px" }}>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Сроки кредитных продуктов, графики погашения:
            </Box>
            <TextField
              value={data.creditPolicy?.tenorsAndRepayment || ""}
              onChange={(e) => updateField("creditPolicy.tenorsAndRepayment", e.target.value)}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          {/* Monitoring */}
          <Box sx={{ marginBottom: "16px" }}>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Мониторинги:
            </Box>
            <TextField
              value={data.creditPolicy?.monitoring || ""}
              onChange={(e) => updateField("creditPolicy.monitoring", e.target.value)}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          {/* Covenants */}
          <Box sx={{ marginBottom: "16px" }}>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Ковенанты (обороты, капитал, продукты в банке, пр.):
            </Box>
            <TextField
              value={data.creditPolicy?.covenants || ""}
              onChange={(e) => updateField("creditPolicy.covenants", e.target.value)}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          {/* Other Banks Relations */}
          <Box sx={{ marginBottom: "16px" }}>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Работа с другими банками:
            </Box>
            <TextField
              value={data.creditPolicy?.otherBanksRelations || ""}
              onChange={(e) => updateField("creditPolicy.otherBanksRelations", e.target.value)}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          {/* Other Conditions */}
          <Box sx={{ marginBottom: "16px" }}>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Прочее:
            </Box>
            <TextField
              value={data.creditPolicy?.otherConditions || ""}
              onChange={(e) => updateField("creditPolicy.otherConditions", e.target.value)}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CreditPolicyTab;
